// src/pages/RecordingDetails.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Translate,
  Assessment,
  ThumbDownAltOutlined,
  ThumbUpAltOutlined,
  Close,
  Article,
  SmartToy
} from '@mui/icons-material';
import {
  Box,
  LinearProgress,
  Paper,
  Container,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Snackbar,
  Drawer,
  Tooltip
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import useRecordingDetails from '../hooks/useRecordingDetails';
import { useUserData } from '../hooks/useUserData';
import RecordingHeader from '../components/RecordingHeader';
import TranscriptionSection from '../components/TranscriptionSection';
import RecordingMetadata from '../components/RecordingMetadata';
import AudioPlayerSection from '../components/AudioPlayerSection';
import InteractiveTranscript from '../components/InteractiveTranscript';
import DeleteRecordingDialog from '../components/DeleteRecordingDialog';
import SetInitialTimeDialog from '../components/SetInitialTimeDialog';
import ActionableTranscriptionSection from '../components/ActionableTranscriptionSection';
import ChatWindow from '../components/ChatWindow';
import ChecklistAnalysisSection from '../components/ChecklistAnalysisSection';
import TalkTimeAnalysis from '../components/TalkTimeAnalysis';
import * as amplitude from '@amplitude/analytics-browser';

const RecordingDetails = () => {
  const { docId } = useParams();
  const decodedDocId = decodeURIComponent(docId);
  const navigate = useNavigate();
  const auth = getAuth();
  const { userData } = useUserData();

  const {
    recordingDetails,
    loading,
    error,
    audioUrl,
    wordSRTUrl,
    deleteRecording,
    updateRecording
  } = useRecordingDetails(decodedDocId, navigate);

  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [initialTimeDialogOpen, setInitialTimeDialogOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState('Պատճենել');
  const [tabValue, setTabValue] = useState(0);
  const [chatInput, setChatInput] = useState('');
  const [chatThinking, setChatThinking] = useState(false);
  const [chatResetting, setChatResetting] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [currentUserMessage, setCurrentUserMessage] = useState('');
  const [messageCopied, setMessageCopied] = useState(null);
  const [audioRef, setAudioRef] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isKaraokeEnabled, setIsKaraokeEnabled] = useState(true);
  const [seekTrigger, setSeekTrigger] = useState(0);

  // Feedback State
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSnackbarOpen, setFeedbackSnackbarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [playerHeight, setPlayerHeight] = useState(118);

  const chatContainerRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const appBarOffset = isMobile ? 56 : 64;
  const measuredPlayerHeight = audioUrl ? playerHeight : 0;
  const bottomDockOffset = measuredPlayerHeight ? measuredPlayerHeight + 10 : 20;
  const floatingButtonBottom = measuredPlayerHeight ? measuredPlayerHeight + 16 : 24;
  const floatingButtonRight = isMobile ? 14 : 22;

  useEffect(() => {
    if (!audioUrl) {
      setPlayerHeight(isMobile ? 112 : 118);
    }
  }, [audioUrl, isMobile]);

  useEffect(() => {
    if (isMobile) {
      setChatExpanded(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const clearScrollLockStyles = () => {
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    };

    clearScrollLockStyles();
    return clearScrollLockStyles;
  }, []);

  const handleSeek = () => {
    setSeekTrigger(Date.now());
  };

  const handleTimeUpdate = useCallback((nextTime) => {
    setCurrentTime((prev) => (Math.abs(prev - nextTime) >= 0.05 ? nextTime : prev));
  }, []);

  const handleTabChange = (_event, newValue) => {
    setTabValue(newValue);
  };

  const handleCopyText = (text) => {
    const finalText = text ? text + `\n\nՁայնագրության հղումը՝ ${window.location.href}` : '';
    navigator.clipboard.writeText(finalText);
    setCopyStatus('Պատճենված է');
    amplitude.track('Text Copied', { docId: decodedDocId });
    setTimeout(() => setCopyStatus('Պատճենել'), 2000);
  };

  const handleCopyMessage = (text, index) => {
    navigator.clipboard.writeText(text);
    setMessageCopied(index);
    amplitude.track('Chat Message Copied', { docId: decodedDocId });
    setTimeout(() => setMessageCopied(null), 2000);
  };

  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
    closeMenu();
    amplitude.track('Delete Dialog Opened', { docId: decodedDocId });
  };
  const closeDeleteDialog = () => setDeleteDialogOpen(false);
  const handleDeleteRecording = async () => {
    await deleteRecording(recordingDetails);
  };

  const openSetInitialTimeDialog = () => {
    setInitialTimeDialogOpen(true);
    closeMenu();
    amplitude.track('Set Initial Time Dialog Opened', { docId: decodedDocId });
  };
  const closeSetInitialTimeDialog = () => setInitialTimeDialogOpen(false);
  const handleSaveInitialTime = async (seconds) => {
    await updateRecording({ initialTimeOffsetSeconds: seconds });
    setInitialTimeDialogOpen(false);
    amplitude.track('Initial Time Updated', { docId: decodedDocId, seconds });
  };

  const handleDownloadSubtitles = async () => {
    if (!wordSRTUrl) return;
    const link = document.createElement('a');
    link.href = wordSRTUrl;
    link.download = `subtitles-${decodedDocId}.srt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    amplitude.track('Subtitles Downloaded', { docId: decodedDocId });
  };

  const handleUpdateTitle = async (newTitle) => {
    await updateRecording({ title: newTitle });
    amplitude.track('Title Updated', { docId: decodedDocId });
  };

  const handleToggleStar = async () => {
    const newStarred = !recordingDetails.starred;
    await updateRecording({ starred: newStarred });
    amplitude.track('Recording Starred', { docId: decodedDocId, starred: newStarred });
  };

  const handleThumbsUp = async () => {
    try {
      await updateRecording({
        resolutionFeedback: 'positive',
        resolutionFeedbackDate: new Date().toISOString()
      });
      setFeedbackSnackbarOpen(true);
      amplitude.track('Summary Feedback Positive', { docId: decodedDocId });
    } catch (err) {
      console.error('Error sending positive feedback:', err);
    }
  };

  const handleSendFeedback = async () => {
    try {
      await updateRecording({
        resolutionFeedback: feedbackText,
        resolutionFeedbackDate: new Date().toISOString()
      });
      setFeedbackDialogOpen(false);
      setFeedbackText('');
      setFeedbackSnackbarOpen(true);
      amplitude.track('Summary Feedback Sent', { docId: decodedDocId });
    } catch (err) {
      console.error('Error sending feedback:', err);
    }
  };

  const handleUpdateSpeakerName = async (speakerId, newName) => {
    await updateRecording({
      speakerNames: {
        ...recordingDetails.speakerNames,
        [speakerId]: newName
      }
    });
    amplitude.track('Speaker Name Updated', { docId: decodedDocId, speakerId });
  };

  const handleSendChatMessage = async () => {
    const message = chatInput.trim();
    if (!message) return;

    amplitude.track('ui_chat', {
      action: 'message_sent',
      transcriptId: decodedDocId,
      docId: decodedDocId,
      userId: auth.currentUser?.uid || 'unknown',
      plan: userData?.plan || 'free',
      messageLength: message.length
    });

    setCurrentUserMessage(message);
    setChatThinking(true);
    setChatInput('');
    try {
      const functions = getFunctions();
      const callChat = httpsCallable(functions, 'recordingChat');
      await callChat({ docId: decodedDocId, message });
    } catch (err) {
      console.error('Chat error:', err);
    } finally {
      setCurrentUserMessage('');
      setChatThinking(false);
    }
  };

  const handleResetChat = async () => {
    if (chatThinking || chatResetting || currentUserMessage) return;

    setChatResetting(true);
    setChatInput('');
    setCurrentUserMessage('');
    setMessageCopied(null);

    try {
      await updateRecording({ chat: [] });
      amplitude.track('ui_chat', {
        action: 'chat_reset',
        transcriptId: decodedDocId,
        docId: decodedDocId,
        userId: auth.currentUser?.uid || 'unknown',
        plan: userData?.plan || 'free'
      });
    } catch (err) {
      console.error('Failed to reset chat:', err);
    } finally {
      setChatResetting(false);
    }
  };

  useEffect(() => {
    amplitude.track('Recording Details Page Viewed', { docId: decodedDocId });
  }, [decodedDocId]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [recordingDetails?.chat, chatThinking, currentUserMessage]);

  useEffect(() => {
    if (recordingDetails?.title) {
      document.title = `${recordingDetails.title} | Xosum.am`;
    } else {
      document.title = 'Ձայնագրության մանրամասներ | Xosum.am';
    }
    return () => {
      document.title = 'Xosum.am';
    };
  }, [recordingDetails?.title]);

  const handleSampleQuestion = (question) => {
    setChatInput(question);
    chatInputRef.current?.focus();
  };

  const openChat = () => setChatOpen(true);
  const closeChat = () => setChatOpen(false);
  const toggleChatExpanded = () => setChatExpanded((prev) => !prev);
  const handlePlayerDownloadAudio = () => {
    amplitude.track('Audio Downloaded', { docId: decodedDocId });
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 4 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: 'center', backgroundColor: '#fff5f5' }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (!recordingDetails) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography variant="h6" color="textSecondary">
            No recording details available.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const {
    status,
    filePath,
    createdAt,
    duration,
    fileType,
    title,
    source: recordingSource,
    diarizationStatus,
    diarizationSegments,
    speakerNames
  } = recordingDetails;
  const isTranscribed = status?.toLowerCase() === 'transcribed';
  const hasSubtitles = Boolean(wordSRTUrl);
  const displayTitle = title || 'Դեռ անվերնագիր ձայնագրություն';
  const chatWindowProps = {
    isTranscribed,
    chat: recordingDetails.chat,
    chatInput,
    setChatInput,
    chatThinking,
    currentUserMessage,
    messageCopied,
    handleCopyMessage,
    handleSendChatMessage,
    handleSampleQuestion,
    chatContainerRef,
    chatEndRef,
    chatInputRef,
    onResetChat: handleResetChat,
    chatResetting,
    canResetChat: Boolean(recordingDetails.chat?.length) && !chatThinking && !currentUserMessage && !chatResetting,
    onToggleExpand: toggleChatExpanded,
    chatExpanded,
    canToggleExpand: !isMobile,
    onClose: closeChat
  };

  return (
    <Box
      sx={{
        minHeight: `calc(100vh - ${appBarOffset}px)`,
        background: 'linear-gradient(180deg, #f4f7fb 0%, #ffffff 45%)',
        pb: `${measuredPlayerHeight ? measuredPlayerHeight + 52 : 40}px`
      }}
    >
      <Container maxWidth="xl" sx={{ pt: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2.25 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)'
          }}
        >
          <RecordingHeader
            onBack={() => navigate(-1)}
            onOpenMenu={openMenu}
            onCloseMenu={closeMenu}
            anchorEl={anchorEl}
            onDelete={openDeleteDialog}
            onSetInitialTime={openSetInitialTimeDialog}
            hasInitialTime={recordingDetails.initialTimeOffsetSeconds != null}
            title={displayTitle}
            onUpdateTitle={handleUpdateTitle}
            starred={recordingDetails.starred || false}
            onToggleStar={handleToggleStar}
          />

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant={isMobile ? 'fullWidth' : 'standard'}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              mb: 2.5,
              p: 0.5,
              borderRadius: 2,
              backgroundColor: 'grey.100',
              minHeight: 50,
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                borderRadius: 1.5,
                fontWeight: 700,
                minHeight: { xs: 40, sm: 44 },
                minWidth: { xs: 0, sm: 90 },
                px: { xs: 0.25, sm: 1.5 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textTransform: 'none'
              },
              '& .Mui-selected': {
                backgroundColor: 'background.paper',
                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)'
              }
            }}
          >
            <Tab
              icon={<Article />}
              label={isMobile ? null : 'Բնօրինակ'}
              aria-label="Բնօրինակ"
              iconPosition={isMobile ? 'top' : 'start'}
            />
            <Tab
              icon={<Translate />}
              label={isMobile ? null : 'Թարգմանություն'}
              aria-label="Թարգմանություն"
              iconPosition={isMobile ? 'top' : 'start'}
            />
            <Tab
              icon={<Assessment />}
              label={isMobile ? null : 'Վերլուծություն'}
              aria-label="Վերլուծություն"
              iconPosition={isMobile ? 'top' : 'start'}
            />
          </Tabs>

          {tabValue === 0 && (
            <Box>
              {wordSRTUrl ? (
                <InteractiveTranscript
                  wordSRTUrl={wordSRTUrl}
                  currentTime={currentTime}
                  audioRef={audioRef}
                  isKaraokeEnabled={isKaraokeEnabled}
                  diarizationStatus={diarizationStatus}
                  diarizationSegments={diarizationSegments}
                  speakerNames={speakerNames}
                  onUpdateSpeakerName={handleUpdateSpeakerName}
                  seekTrigger={seekTrigger}
                  initialTimeOffsetSeconds={recordingDetails.initialTimeOffsetSeconds ?? null}
                  recordingMetadata={{
                    title: recordingDetails.title,
                    createdAt: recordingDetails.createdAt,
                    docId: decodedDocId,
                    filePath: recordingDetails.filePath,
                    source: recordingDetails.source,
                  }}
                  onDownloadPDF={() => amplitude.track('Transcript PDF Downloaded', { docId: decodedDocId })}
                />
              ) : (
                <TranscriptionSection
                  title="Վերծանված տեքստ"
                  transcription={recordingDetails.transcription}
                  copyStatus={copyStatus}
                  onCopy={handleCopyText}
                  onSave={(newText) => updateRecording({ transcription: newText })}
                />
              )}

              <Box sx={{ mt: 2 }}>
                <RecordingMetadata
                  filePath={filePath}
                  docId={decodedDocId}
                  duration={duration}
                  createdAt={createdAt}
                  fileType={fileType}
                  status={status}
                  error={recordingDetails.error}
                  source={recordingSource || 'upload'}
                  userData={userData}
                />
              </Box>
            </Box>
          )}

          {tabValue === 1 && (
            <Box sx={{ pb: 0.5 }}>
              <ActionableTranscriptionSection
                docId={decodedDocId}
                field="translation"
                title="Թարգմանություն"
                firebaseFunctionName="makeTranslation"
                trackEvent="Translation Created"
                icon={Translate}
                emptyTitle="Թարգմանություն"
                emptyDescription="Այստեղ կարող եք թարգմանել վերծանված թեքստը այլ լեզուներով:"
                buttonText="Թարգմանել"
                transcription={recordingDetails.translation}
                copyStatus={copyStatus}
                onCopy={handleCopyText}
                onSave={(newText) => updateRecording({ translation: newText })}
                isTranscribed={isTranscribed}
                withLanguageSelector
                inProgress={recordingDetails.translationInProgress}
              />
            </Box>
          )}

          {tabValue === 2 && (
            <Box sx={{ display: 'grid', gap: 2.5 }}>
              {recordingDetails.resolution && (
                <Card
                  elevation={0}
                  sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: 'info.soft',
                        border: '1px solid',
                        borderColor: 'info.main',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        position: 'relative'
                      }}
                    >
                      <Assessment color="info" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" color="info.main" gutterBottom sx={{ fontWeight: 700 }}>
                          Հիմնական միտքը
                        </Typography>
                        <Typography variant="body1" color="text.primary">
                          {recordingDetails.resolution}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={handleThumbsUp}
                          sx={{
                            color: 'success.main',
                            opacity: 0.75,
                            '&:hover': { opacity: 1, bgcolor: 'success.soft' }
                          }}
                        >
                          <ThumbUpAltOutlined fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setFeedbackDialogOpen(true)}
                          sx={{
                            color: 'info.main',
                            opacity: 0.75,
                            '&:hover': { opacity: 1, bgcolor: 'info.soft' }
                          }}
                        >
                          <ThumbDownAltOutlined fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  </CardContent>
                </Card>
              )}
              <ChecklistAnalysisSection
                docId={decodedDocId}
                isTranscribed={isTranscribed}
              />
              <TalkTimeAnalysis
                diarizationSegments={diarizationSegments}
                speakerNames={speakerNames}
              />
            </Box>
          )}
        </Paper>

        <DeleteRecordingDialog
          open={deleteDialogOpen}
          onClose={closeDeleteDialog}
          onConfirm={handleDeleteRecording}
        />
        <SetInitialTimeDialog
          open={initialTimeDialogOpen}
          onClose={closeSetInitialTimeDialog}
          onSave={handleSaveInitialTime}
          currentValueSeconds={recordingDetails.initialTimeOffsetSeconds ?? null}
        />
      </Container>

      {chatExpanded && !isMobile ? (
        <Dialog
          open={chatOpen}
          onClose={closeChat}
          maxWidth={false}
          fullWidth
          disableScrollLock
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(15, 23, 42, 0.45)'
            }
          }}
          PaperProps={{
            sx: {
              width: { sm: 'min(92vw, 980px)', md: 'min(86vw, 1200px)' },
              height: 'min(86dvh, 920px)',
              maxHeight: 'calc(100dvh - 40px)',
              m: { sm: 2.5 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 28px 72px rgba(15, 23, 42, 0.35)',
              overflow: 'hidden'
            }
          }}
        >
          <ChatWindow {...chatWindowProps} />
        </Dialog>
      ) : (
        <Drawer
          anchor="right"
          open={chatOpen}
          onClose={closeChat}
          ModalProps={{
            keepMounted: true,
            disableScrollLock: !isMobile
          }}
          PaperProps={{
            sx: {
              top: { xs: 0, sm: `${appBarOffset}px` },
              bottom: { xs: 0, sm: `${bottomDockOffset}px` },
              height: { xs: '100dvh', sm: 'auto' },
              width: { xs: '100vw', sm: 420, md: 460 },
              maxWidth: '100%',
              borderTopLeftRadius: { xs: 0, sm: 3 },
              borderBottomLeftRadius: { xs: 0, sm: 3 },
              boxShadow: { xs: 'none', sm: '0 18px 48px rgba(15, 23, 42, 0.24)' },
              borderLeft: { xs: 'none', sm: '1px solid' },
              borderColor: 'divider'
            }
          }}
          sx={{
            zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(15, 23, 42, 0.3)'
            }
          }}
        >
          <ChatWindow {...chatWindowProps} />
        </Drawer>
      )}

      {!chatOpen && (
        <Tooltip title="Բացել AI օգնականը">
          <Button
            variant="contained"
            startIcon={isMobile ? null : <SmartToy />}
            onClick={openChat}
            aria-label="open ai chat"
            sx={{
              position: 'fixed',
              right: floatingButtonRight,
              bottom: floatingButtonBottom,
              zIndex: (muiTheme) => muiTheme.zIndex.modal + 2,
              borderRadius: '999px',
              minHeight: { xs: 46, sm: 42 },
              minWidth: { xs: 46, sm: 136 },
              width: { xs: 46, sm: 'auto' },
              px: { xs: 0, sm: 2.25 },
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: 'primary.main',
              boxShadow: '0 12px 26px rgba(25, 118, 210, 0.35)'
            }}
          >
            {isMobile ? <SmartToy fontSize="small" /> : 'Հարց AI-ին'}
          </Button>
        </Tooltip>
      )}

      {/* Feedback Dialog */}
      <Dialog open={feedbackDialogOpen} onClose={() => setFeedbackDialogOpen(false)} disableScrollLock maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Ինչն է սխալ համառոտագրության մեջ՞
          <IconButton onClick={() => setFeedbackDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="feedback"
            label="Մեկնաբանություն"
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Օրինակ՝ բաց է թողնված կարևոր դետալ..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFeedbackDialogOpen(false)} color="inherit">
            Չեղարկել
          </Button>
          <Button onClick={handleSendFeedback} variant="contained" disabled={!feedbackText.trim()}>
            Ուղարկել
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={feedbackSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setFeedbackSnackbarOpen(false)}
        message="Շնորհակալություն կարծիքի համար"
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={() => setFeedbackSnackbarOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        }
      />

      {audioUrl && (
        <AudioPlayerSection
          audioUrl={audioUrl}
          onTimeUpdate={handleTimeUpdate}
          setAudioRef={setAudioRef}
          onDownloadAudio={handlePlayerDownloadAudio}
          onDownloadSubtitles={handleDownloadSubtitles}
          hasSubtitles={hasSubtitles}
          onHeightChange={setPlayerHeight}
          recordingMetadata={recordingDetails}
          isKaraokeEnabled={isKaraokeEnabled}
          setIsKaraokeEnabled={setIsKaraokeEnabled}
          onSeek={handleSeek}
          initialTimeOffsetSeconds={recordingDetails.initialTimeOffsetSeconds ?? null}
        />
      )}
    </Box>
  );
};

export default RecordingDetails;
